// ===================================================
// useSwipeBack.js — 화면 왼쪽 끝에서 오른쪽으로 스와이프하면 뒤로가는 훅
//
// iOS 네이티브 앱처럼 왼쪽 가장자리(40px 이내)에서
// 오른쪽으로 80px 이상 스와이프하면 onBack 콜백을 호출합니다.
//
// 사용법:
//   useSwipeBack({ onBack: handleHome, enabled: screen !== 'home' });
// ===================================================

import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

const EDGE_THRESHOLD = 40;   // 이 픽셀 이내에서 시작해야 스와이프로 인식
const SWIPE_THRESHOLD = 80;  // 이 픽셀 이상 오른쪽으로 이동해야 뒤로가기 실행

export function useSwipeBack({ onBack, enabled = true }) {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    // 네이티브 앱에서만 동작 (웹 브라우저는 뒤로가기 버튼이 있으므로 불필요)
    if (!Capacitor.isNativePlatform()) return;
    if (!enabled) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      // 화면 왼쪽 가장자리에서 시작한 터치만 추적
      if (touch.clientX <= EDGE_THRESHOLD) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
      } else {
        touchStartX.current = null;
      }
    };

    const handleTouchEnd = (e) => {
      if (touchStartX.current === null) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);

      // 수평 이동이 수직 이동보다 크고, 충분히 오른쪽으로 스와이프했을 때
      if (deltaX > SWIPE_THRESHOLD && deltaX > deltaY * 1.5) {
        onBack?.();
      }

      touchStartX.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onBack, enabled]);
}
